import {Component, Inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {PlanningItemHelper} from '@application/helper/planning-prototype/planning-item-helper';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {OrderCarpet} from '@domain/planning-prototype/carpet/order-carpet';
import {ProductionOrder} from '@domain/planning-prototype/carpet/production-order';
import {GenericOrder} from '@domain/planning-prototype/generic-order.interface';
import {PlanningEquipment} from '@domain/planning-prototype/planning-equipment';
import {PlanningItem} from '@domain/planning-prototype/planning-item';
import {PlanningItemForecast} from '@domain/planning-prototype/planning-item-forecast';
import {OrderPlastic} from '@domain/planning-prototype/plastic/order-plastic';
import {ProductionOrderPlastic} from '@domain/planning-prototype/plastic/production-order-plastic';
import {ReportItemField} from '@domain/planning-prototype/real-time/report-item-field.enum';
import {ReportItemUpdate} from '@domain/planning-prototype/real-time/report-item-update';
import {OrderTufting} from '@domain/planning-prototype/tufting/order-tufting';
import {OrderWeaving} from '@domain/planning-prototype/weaving/order-weaving';
import {ProductionScheduleState} from '@domain/production-schedule/production-schedule-state';
import {ProductionScheduleStatus} from '@domain/production-schedule/production-schedule-status.enum';
import {Subscription} from '@domain/profile/subscription';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {SignalRPlanningPrototypeService} from '@infrastructure/signalr/planning-prototype/signalr-planning-prototype.service';
import {AssertionUtils, BaseComponent} from '@vdw/angular-component-library';
import moment, {Moment, duration} from 'moment';
import {Observable, combineLatest, concat, distinctUntilChanged, filter, finalize, map, of, startWith, switchMap, takeUntil} from 'rxjs';
import {PlanningPrototypeContextService} from '../../utilities/planning-prototype-context.service';
import {ProductionOrderUtilities} from '../../utilities/production-order-utilities.service';
import {PlanningItemSidebarComponent} from '../planning-item-sidebar/planning-item-sidebar.component';
import {OrderProgress} from './order-progress';

@Component({
  selector: 'app-planning-order-sidebar',
  templateUrl: './planning-order-sidebar.component.html',
  styleUrls: ['./planning-order-sidebar.component.scss']
})
export class PlanningOrderSidebarComponent extends BaseComponent implements OnInit {
  public readonly DURATION_FORMAT_STRING = 'd[d] h[h] m[m]';
  public readonly DURATION_FORMAT_OPTIONS: moment.DurationFormatSettings = {trim: 'both'};
  private isReconfirming = false;
  private currentSubscription: Subscription;

  public readonly realTimeProgress = combineLatest([this.planningContext.selectedItemChanges, this.planningContext.planningLineChanges.pipe(startWith(of(null)))]).pipe(
    distinctUntilChanged(),
    filter(([selectedItem, _]: [PlanningItem, any]) => PlanningItemHelper.isOrderItem(selectedItem)),
    switchMap(([order, _]: [GenericOrder, any]) => this.getRealTimeProgress(order)),
    takeUntilDestroyed()
  );

  public get order(): GenericOrder {
    return this.parent.planningItem as any;
  }

  public get machine(): PlanningEquipment {
    return this.parent.machine;
  }

  public get forecast(): PlanningItemForecast {
    return this.planningContext.sortedPlanningItemForecasts[this.order.draftId];
  }

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly parent: PlanningItemSidebarComponent,
    private readonly planningContext: PlanningPrototypeContextService,
    private readonly productionOrderUtilities: ProductionOrderUtilities,
    private readonly realTimePlanning: SignalRPlanningPrototypeService,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit(): void {
    this.currentSubscription = this.authentication.getCurrentSubscription();
  }

  public getProductionTime(): string {
    return duration(this.order.estimatedProductionTime).format(this.DURATION_FORMAT_STRING, this.DURATION_FORMAT_OPTIONS);
  }

  public getDuration(): string {
    let end: Moment;
    if (AssertionUtils.isNullOrUndefined(this.order.actualEndDate)) {
      end = moment();
    } else {
      end = moment(this.order.actualEndDate);
    }
    return duration(end.diff(moment(this.order.actualStartDate))).format(this.DURATION_FORMAT_STRING, this.DURATION_FORMAT_OPTIONS);
  }

  public getIconNameForProductionOrderStatus(): string {
    return ProductionScheduleStatus.getStatusIconName(this.getProductionOrderStatus());
  }

  public getIsDifferentConfiguration(): boolean {
    return this.planningContext.productChanges.includes(this.order.draftId);
  }

  public getIsPastDue(): boolean {
    if (AssertionUtils.isNullOrUndefined(this.order.productionOrder?.dueDate)) {
      return false;
    }
    return PlanningItemHelper.getTimelineEndDate(this.order, this.forecast) > this.order.productionOrder?.dueDate;
  }

  public getQuantityUnit(): string {
    if (this.isTufting(this.order) || this.isWeaving(this.order)) {
      return 'GENERAL.UNIT.METER';
    }
    if (this.isPlastic(this.order)) {
      return `GENERAL.UNIT.${this.order.productionOrder.plasticProduct.unitOfMeasurement}`;
    }

    return 'GENERAL.UNIT.PICKS';
  }

  public openOrderDetails(): void {
    let url: string;
    if (this.isCarpet(this.order)) {
      url = RouteUtils.paths.texFab.productionOrder.editProductionOrder.absolutePath;
    } else if (this.isWeaving(this.order)) {
      url = RouteUtils.paths.texFab.weaving.editProductionOrder.absolutePath;
    } else if (this.isTufting(this.order)) {
      url = RouteUtils.paths.texFab.tufting.editProductionOrder.absolutePath;
    } else if (this.isPlastic(this.order)) {
      url = RouteUtils.paths.texFab.plastic.editProductionOrder.absolutePath;
    }

    this.router.navigate([url.replace(':id', this.order.productionOrder.id.toString())]);
  }

  public isCarpet(order: PlanningItem): order is OrderCarpet {
    return order instanceof OrderCarpet;
  }

  public isWeaving(order: PlanningItem): order is OrderWeaving {
    return order instanceof OrderWeaving;
  }

  public isTufting(order: PlanningItem): order is OrderTufting {
    return order instanceof OrderTufting;
  }

  public isPlastic(order: PlanningItem): order is OrderPlastic {
    return order instanceof OrderPlastic;
  }

  public getProductionOrderStatus(): ProductionScheduleStatus {
    return PlanningItemHelper.getItemStatus(this.order);
  }

  public getIsStarted(): boolean {
    return !AssertionUtils.isNullOrUndefined(this.order.actualStartDate);
  }

  public delete(): void {
    const parentRun = this.planningContext.planningLinesByMachine[this.parent.machine.id].planningItems.find((item: PlanningItem) => item.planningItems.includes(this.order));
    this.planningContext.delete(this.order, parentRun.planningItems, this.parent.machine);
  }

  public getProductionOrderState(): ProductionScheduleState {
    return !AssertionUtils.isNullOrUndefined(this.order.productionOrder) && this.order.productionOrder instanceof ProductionOrderPlastic ? this.order.productionOrder.state : null;
  }

  private getRealTimeProgress(order: GenericOrder): Observable<OrderProgress> {
    const genericProgress = of({
      completionPercent: this.getProgress(order),
      quantityProduced: this.getQuantityProduced(order),
      quantityRequired: this.getQuantityRequired(order)
    } as OrderProgress);

    if (!order.isActive) {
      return genericProgress;
    }

    const requestedReportItems = [ReportItemField.ORDER_PROGRESS];
    if (order instanceof OrderCarpet) {
      requestedReportItems.push(ReportItemField.PICKS_PRODUCED, ReportItemField.PICKS_REQUIRED);
    } else {
      requestedReportItems.push(ReportItemField.QTY_PRODUCED, ReportItemField.QTY_REQUIRED);
    }

    const machine = this.planningContext.machinesByPlanningItem[order.draftId];
    const realTimeUpdates = combineLatest(requestedReportItems.map((field: ReportItemField) => this.realTimePlanning.getReportItemUpdates(machine.id, field))).pipe(
      map(
        ([progress, quantityProduced, quantityRequired]: [ReportItemUpdate<number>, ReportItemUpdate<number>, ReportItemUpdate<number>]) =>
          ({
            completionPercent: progress.value,
            quantityProduced: quantityProduced.value,
            quantityRequired: quantityRequired.value
          }) as OrderProgress
      )
    );

    return concat(genericProgress, realTimeUpdates);
  }

  private getProgress(order: GenericOrder): number {
    return (this.getQuantityProduced(order) / this.getQuantityRequired(order)) * 100;
  }

  private getQuantityRequired(order: GenericOrder): number {
    if (this.isTufting(order) || this.isWeaving(order)) {
      return order.productionOrder.plannedQuantityInMeters;
    }

    if (this.isPlastic(order)) {
      return order.productionOrder.plannedQuantity;
    }

    return order.productionOrder?.plannedQuantityInPicks;
  }

  private getQuantityProduced(order: GenericOrder): number {
    if (this.isTufting(order) || this.isWeaving(order)) {
      return order.producedQuantityInMeters;
    }

    if (this.isPlastic(order)) {
      return order.producedQuantity;
    }

    return order.producedQuantityInPicks;
  }

  public reconfirmOrder(): void {
    if (!(this.order.productionOrder instanceof ProductionOrder) || this.isReconfirming) {
      return;
    }
    this.isReconfirming = true;
    this.productionOrderUtilities
      .reconfirmOrder(this.order.productionOrder)
      .pipe(
        finalize(() => (this.isReconfirming = false)),
        takeUntil(this.unSubscribeOnViewDestroy),
        filter((confirmed: boolean) => confirmed)
      )
      .subscribe(() => {
        this.order.productionOrder.status = ProductionScheduleStatus.VERIFIED;
      });
  }
}
